import { Smartphone } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OtpVerificationCard from '@/domains/auth/components/OtpVerificationCard/OtpVerificationCard';
import usePhoneVerification from '@/domains/auth/hooks/usePhoneVerification';
import { apiErrorMessage } from '@/domains/auth/utils/apiError';
import { maskPhone, normalizePhone } from '@/domains/auth/utils/phone';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useChangePhone, useProfile } from '@/domains/mobile-web/hooks/useProfile';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { showToast } from '@/shared/utils/showToast';

const PHONE_ICON = <Smartphone className="size-8 text-black" aria-hidden="true" />;

// Edit Profil > Ubah Nomor HP: the new number, then the WhatsApp (or SMS) code sent to it.
// A matching code verifies the number in the server session; PUT /profile/phone then saves it.
const ChangePhone = () => {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const changePhone = useChangePhone();
  const { turnstileRef, sent, send, verify, resendIn, isSending, isVerifying, error, setError } =
    usePhoneVerification();
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [code, setCode] = useState('');
  // The code step is shown once a code for a phone change went out
  const [showCode, setShowCode] = useState(false);
  const changeSent = showCode && sent?.purpose === 'change_phone' ? sent : null;

  const onSendCode = async (event: FormEvent) => {
    event.preventDefault();
    const phone = normalizePhone(phoneInput);
    if (!phone) {
      setPhoneError('Nomor HP tidak valid. Contoh: 085776235639.');
      return;
    }
    if (phone === profile?.phone_number) {
      setPhoneError('Nomor HP ini sudah terdaftar di akun Anda.');
      return;
    }
    setPhoneError('');
    // Back from the code step with the same number: the code already sent is still the one to enter
    if (sent?.purpose === 'change_phone' && sent.phone === phone && resendIn > 0) {
      setShowCode(true);
      return;
    }
    try {
      await send({ purpose: 'change_phone', phone_number: phone });
      setCode('');
      setShowCode(true);
    } catch (err) {
      setPhoneError(apiErrorMessage(err, 'Kode tidak dapat dikirim. Silakan coba lagi.'));
    }
  };

  const onResend = async () => {
    if (!changeSent) return;
    try {
      await send({ purpose: 'change_phone', phone_number: changeSent.phone });
      setCode('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Kode tidak dapat dikirim. Silakan coba lagi.'));
    }
  };

  const onVerify = async () => {
    if (!(await verify(code))) return;
    changePhone.mutate(undefined, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Nomor HP diubah',
          message: 'Nomor HP baru berhasil disimpan.',
        });
        navigate(-1);
      },
      onError: (err) => setError(apiErrorMessage(err, 'Nomor HP tidak dapat diubah.')),
    });
  };

  const onBack = () => {
    if (changeSent) {
      setShowCode(false);
      setCode('');
      setError('');
      return;
    }
    navigate(-1);
  };

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" title="Ubah Nomor HP" onBack={onBack} />
      </HeaderMobile>

      <WrapperMobile>
        {changeSent ? (
          <OtpVerificationCard
            icon={PHONE_ICON}
            title="Verifikasi Nomor HP Baru"
            phone={changeSent.phone}
            channel={changeSent.channel}
            code={code}
            onCodeChange={setCode}
            onSubmit={onVerify}
            onResend={onResend}
            resendIn={resendIn}
            error={error}
            isSending={isSending}
            isSubmitting={isVerifying || changePhone.isPending}
          />
        ) : (
          <form
            onSubmit={onSendCode}
            noValidate
            className="shadowCard h-fit p-6 flex-col flex gap-6 rounded-[24px]"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              {PHONE_ICON}
              <p className="title2medium text-black">Nomor HP Baru</p>
              <p className="reg12 text-text-grey">
                Kami akan mengirim kode verifikasi ke nomor baru anda.
                {profile?.phone_number && ` Nomor saat ini: ${maskPhone(profile.phone_number)}.`}
              </p>
            </div>

            <InputMobile
              label="Nomor HP Baru"
              name="phone_number"
              type="tel"
              inputMode="numeric"
              placeholder="Contoh: 085776235639"
              autoComplete="tel"
              value={phoneInput}
              onChange={(event) => {
                setPhoneInput(event.target.value);
                setPhoneError('');
              }}
              error={phoneError}
              helperText="Pastikan nomor anda aktif dan dapat menerima pesan."
            />

            <Button
              type="submit"
              customButton="btn-fill-blue"
              disabled={!phoneInput.trim() || isSending}
            >
              <span className="title2medium text-white">
                {isSending ? 'Memproses...' : 'Kirim kode Verifikasi'}
              </span>
            </Button>
          </form>
        )}
        {/* One widget host for both steps, so Turnstile is not rendered twice */}
        <div ref={turnstileRef} className="flex justify-center empty:hidden" />
      </WrapperMobile>
    </main>
  );
};

export default ChangePhone;
